import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  HandThumbUpIcon,
  LifebuoyIcon,
  SparklesIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { supportAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useLicense } from '../hooks/useLicense';
import InsightsTabs from '../components/common/InsightsTabs';

const DEMO_SUPPORT_INSIGHTS = {
  period_days: 30,
  generated_at: new Date().toISOString(),
  service_health: 'needs_attention',
  summary: {
    total_tickets: 42,
    open: 6,
    in_progress: 3,
    resolved: 25,
    deflected: 8,
    urgent_open: 1,
    unassigned_open: 4,
    open_24h_plus: 3,
    avg_resolution_hours: 5.6,
    deflection_rate: 0.19,
    csat: { positive: 22, negative: 5, rated: 27, rate: 0.81 },
    sla: { first_response_breached: 2, resolution_breached: 1 },
  },
  top_issue_themes: [
    {
      theme: 'Booking or visit follow-up',
      count: 13,
      open: 4,
      resolved: 7,
      deflected: 2,
      sample: 'Customer asked for a visit confirmation but did not get a callback.',
      handled_pct: 69,
      csat_up: 5,
      csat_down: 3,
    },
    {
      theme: 'Pricing and payment questions',
      count: 10,
      open: 2,
      resolved: 6,
      deflected: 2,
      sample: 'Customer needed clarification on payment schedule and charges.',
      handled_pct: 80,
      csat_up: 8,
      csat_down: 1,
    },
    {
      theme: 'Document or brochure requests',
      count: 8,
      open: 1,
      resolved: 5,
      deflected: 2,
      sample: 'Customer asked for updated brochure and floor plan details.',
      handled_pct: 88,
      csat_up: 6,
      csat_down: 0,
    },
  ],
  recommended_actions: [
    {
      label: 'Clear the 24h backlog',
      detail: '3 open ticket(s) are older than 24 hours.',
      tone: 'warning',
    },
    {
      label: 'Assign ownership',
      detail: '4 open ticket(s) are unassigned.',
      tone: 'warning',
    },
    {
      label: 'Improve support content for booking or visit follow-up',
      detail: '13 ticket(s) in this period mention this theme.',
      tone: 'info',
    },
  ],
};

const healthTone = {
  healthy: {
    label: 'Healthy',
    className: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-900/60',
  },
  needs_attention: {
    label: 'Needs attention',
    className: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-900/60',
  },
  critical: {
    label: 'Critical',
    className: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-900/60',
  },
};

const actionTone = {
  healthy: 'border-green-200 bg-green-50 text-green-800 dark:border-green-900/60 dark:bg-green-900/20 dark:text-green-200',
  info: 'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-900/60 dark:bg-sky-900/20 dark:text-sky-200',
  warning: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-900/20 dark:text-amber-200',
  critical: 'border-red-200 bg-red-50 text-red-800 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-200',
};

function pct(value) {
  if (value == null) return '-';
  return `${Math.round(Number(value) * 100)}%`;
}

function num(value) {
  if (value == null) return '-';
  return Number(value).toLocaleString();
}

function buildPath(tenantId, path) {
  return tenantId ? `/t/${tenantId}${path}` : `/platform${path}`;
}

export default function SupportInsightsPage() {
  const { tenantId } = useParams();
  const currentUser = useAuthStore((state) => state.user);
  const { hasFeature, isLoading: licenseLoading } = useLicense();
  const isSuperadmin = currentUser?.role === 'superadmin' || currentUser?.is_superuser;
  const previewMode = !licenseLoading && !hasFeature('ai_insights') && !isSuperadmin;
  const [days, setDays] = useState(30);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const params = useMemo(() => {
    const query = { days };
    if (tenantId) query.tenant_id = tenantId;
    return query;
  }, [tenantId, days]);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await supportAPI.getInsights(params);
      setInsights(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Could not load support insights');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (previewMode) {
      setInsights({ ...DEMO_SUPPORT_INSIGHTS, period_days: days });
      setLoading(false);
      setError(null);
      return;
    }
    if (licenseLoading) {
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, previewMode, licenseLoading]);

  const summary = insights?.summary || {};
  const health = healthTone[insights?.service_health] || healthTone.healthy;
  const cards = [
    {
      label: 'Support tickets',
      value: num(summary.total_tickets),
      hint: `Last ${insights?.period_days || days} days`,
      icon: LifebuoyIcon,
      tone: 'text-amber-600',
    },
    {
      label: 'Answered by AI alone',
      value: pct(summary.deflection_rate),
      hint: 'No one on your team needed',
      icon: SparklesIcon,
      tone: 'text-indigo-600',
    },
    {
      label: 'Waiting 24h+',
      value: num(summary.open_24h_plus),
      hint: 'Needs follow-up',
      icon: ClockIcon,
      tone: 'text-red-600',
    },
    {
      label: 'Waiting, no owner',
      value: num(summary.unassigned_open),
      hint: 'No owner yet',
      icon: UserGroupIcon,
      tone: 'text-sky-600',
    },
    {
      label: 'Past response-time target',
      value: num((summary.sla?.first_response_breached || 0) + (summary.sla?.resolution_breached || 0)),
      hint: 'First reply or fix is overdue',
      icon: ClockIcon,
      tone: 'text-red-600',
    },
    {
      label: 'Customer satisfaction',
      value: summary.csat?.rate != null ? pct(summary.csat.rate) : '-',
      hint: `${num(summary.csat?.rated || 0)} rated`,
      icon: HandThumbUpIcon,
      tone: 'text-green-600',
    },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <InsightsTabs />
      <div className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 via-white to-amber-50 p-6 shadow-sm dark:border-indigo-900/50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-white px-3 py-1 text-xs font-semibold text-indigo-700 shadow-sm dark:border-indigo-900 dark:bg-gray-800 dark:text-indigo-300">
              <SparklesIcon className="h-4 w-4" />
              Serviti AI support intelligence
            </div>
            <h1 className="mt-4 text-3xl font-semibold tracking-tight text-gray-950 dark:text-white">
              Serviti AI Support Insights
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
              Turn support tickets into service themes, backlog risks, and the next best actions for your team. Not just ticket capture - AI-assisted support improvement.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {[7, 30, 90].map((option) => (
              <button
                key={option}
                onClick={() => setDays(option)}
                className={`rounded-lg px-3 py-2 text-sm font-medium ${
                  days === option
                    ? 'bg-indigo-600 text-white'
                    : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {option}d
              </button>
            ))}
            <button
              onClick={load}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {previewMode && (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-5 text-indigo-900 shadow-sm dark:border-indigo-900/60 dark:bg-indigo-900/20 dark:text-indigo-100">
          <p className="text-sm font-semibold uppercase tracking-wide opacity-80">Preview mode</p>
          <p className="mt-1 text-lg font-bold">Serviti AI Support Insights is part of the AI Insights add-on (included in Scale)</p>
          <p className="mt-2 max-w-3xl text-sm leading-6 opacity-90">
            The ticket themes, service health, backlog risks and recommended actions below are sample data to show what your view will look like. Add AI Insights to see real support insights from your own tickets.
          </p>
          <Link
            to={buildPath(tenantId, '/plan-usage')}
            className="mt-4 inline-flex rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            Talk to sales
          </Link>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        <div className={`rounded-xl border p-4 xl:col-span-1 ${health.className}`}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Service health</span>
            {insights?.service_health === 'critical' ? (
              <ExclamationTriangleIcon className="h-5 w-5" />
            ) : (
              <CheckCircleIcon className="h-5 w-5" />
            )}
          </div>
          <div className="mt-3 text-2xl font-semibold">{loading ? '-' : health.label}</div>
          <p className="mt-1 text-xs opacity-80">Based on urgent, unassigned, and aging tickets.</p>
        </div>
        {cards.map((card) => (
          <div key={card.label} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">{card.label}</span>
              <card.icon className={`h-5 w-5 ${card.tone}`} />
            </div>
            <div className="mt-3 text-2xl font-semibold text-gray-950 dark:text-white">{loading ? '-' : card.value}</div>
            <p className="mt-1 text-xs text-gray-400">{card.hint}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-950 dark:text-white">Top support themes</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Recurring issues your team can fix once instead of answering repeatedly.</p>
            </div>
            <ArrowTrendingUpIcon className="h-6 w-6 text-indigo-600" />
          </div>
          <div className="mt-5 space-y-3">
            {loading ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">Loading themes...</div>
            ) : (insights?.top_issue_themes || []).length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 p-5 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                No repeat support themes yet. Once tickets arrive, Serviti will group them here.
              </div>
            ) : (
              insights.top_issue_themes.map((theme) => (
                <div key={theme.theme} className="rounded-lg border border-gray-100 p-4 dark:border-gray-700">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-medium text-gray-950 dark:text-white">{theme.theme}</h3>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{theme.sample || 'No sample available'}</p>
                    </div>
                    <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                      {theme.count} tickets
                    </span>
                  </div>
                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                    <div
                      className="h-full rounded-full bg-indigo-600"
                      style={{ width: `${Math.min(100, theme.handled_pct || 0)}%` }}
                    />
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs text-gray-400">
                    <span>{theme.handled_pct ?? 0}% resolved or AI-deflected</span>
                    {(theme.csat_up || theme.csat_down) ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="text-green-600 dark:text-green-400">👍 {theme.csat_up || 0}</span>
                        <span className="text-red-500 dark:text-red-400">👎 {theme.csat_down || 0}</span>
                      </span>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div>
            <h2 className="text-lg font-semibold text-gray-950 dark:text-white">Recommended actions</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">What Serviti AI thinks your support team should do next.</p>
          </div>
          <div className="mt-5 space-y-3">
            {loading ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">Loading actions...</div>
            ) : (
              (insights?.recommended_actions || []).map((action) => (
                <div key={`${action.label}-${action.detail}`} className={`rounded-lg border p-4 ${actionTone[action.tone] || actionTone.info}`}>
                  <h3 className="font-medium">{action.label}</h3>
                  <p className="mt-1 text-sm opacity-85">{action.detail}</p>
                </div>
              ))
            )}
          </div>

          <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
            <h3 className="font-semibold text-gray-950 dark:text-white">Why this matters</h3>
            <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
              Your team does not only get a ticket list. Serviti shows which issues repeat, which tickets are aging, where AI deflection is working, and what knowledge needs to be improved next.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to={buildPath(tenantId, '/support')}
                className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                Open tickets
              </Link>
              <Link
                to={buildPath(tenantId, '/rag/sources')}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-white dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                Improve knowledge
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
